const TheContainer = () => import("@/containers/TheContainer.vue");

// Index wrapper that handles scope extraction from route
const AccountingIndex = () => import("@/domain/accounting/index.vue");

// Detail components (transactions)
const OrderItemDetail = () => import("@/domain/accounting/order/OrderItemDetail.vue");
const OrderDetail = () => import("@/domain/accounting/order/OrderDetail.vue");
const ServiceOrderDetail = () => import("@/domain/accounting/details/ServiceOrderDetail.vue");
const RefundDetail = () => import("@/domain/accounting/details/RefundDetail.vue");
const PayoutDetail = () => import("@/domain/accounting/details/PayoutDetail.vue");

// Movement components
const MovementIndex = () => import("@/domain/accounting/movement/MovementIndex.vue");
const MovementOrderItemDetail = () => import("@/domain/accounting/movement/MovementOrderItemDetail.vue");
const MovementRefundDetail = () => import("@/domain/accounting/movement/MovementRefundDetail.vue");

export default [
    // Global scope (admin)
    {
        path: '/admin/accounting',
        component: TheContainer,
        meta: { perms: ['admin.accounting.transactions.list'] },
        children: [
            {
                path: '',
                name: 'admin.accounting',
                component: AccountingIndex,
                meta: {
                    label: 'Accounting',
                    scopeType: 'global',
                    permission: 'admin.accounting.transactions.list'
                },
            },
            {
                path: 'order/:id',
                name: 'admin.accounting.order',
                component: OrderDetail,
                meta: {
                    label: 'Order Detail',
                    scopeType: 'global',
                    permission: 'admin.accounting.order.detail'
                },
            },
            {
                path: 'order-item/:id',
                name: 'admin.accounting.order-item',
                component: OrderItemDetail,
                meta: {
                    label: 'Order Item Detail',
                    scopeType: 'global',
                    permission: 'admin.accounting.order.detail'
                },
            },
            {
                path: 'service-order/:id',
                name: 'admin.accounting.service-order',
                component: ServiceOrderDetail,
                meta: {
                    label: 'Service Order Detail',
                    scopeType: 'global',
                    permission: 'admin.accounting.service_order.detail'
                },
            },
            {
                path: 'refund/:id',
                name: 'admin.accounting.refund',
                component: RefundDetail,
                meta: {
                    label: 'Refund detail',
                    scopeType: 'global',
                    permission: 'admin.accounting.refund.detail'
                },
            },
            {
                path: 'payout/:id',
                name: 'admin.accounting.payout',
                component: PayoutDetail,
                meta: {
                    label: 'Payout Detail',
                    scopeType: 'global',
                    permission: 'admin.accounting.payout.detail'
                },
            },
        ]
    },

    // Contract Manager scope — Transactions
    {
        path: '/contract-manager/:contractManagerId/accounting',
        component: TheContainer,
        meta: {
            perms: ['contract_manager.accounting.transactions.list'],
            breadcrumb: (context) => {
                const cm = context.store.getters['contractManager/contract_manager']
                const contractManagerId = context.route.params.contractManagerId

                if (!cm || cm.id !== parseInt(contractManagerId)) {
                    context.store.dispatch('contractManager/fetchContractManager', contractManagerId)
                        .catch(() => {})
                }

                return [
                    { to: { name: 'index' }, text: context.gettext('Dashboard') },
                    { to: { name: 'contract_manager.list' }, text: 'Contract Managers' },
                    {
                        to: { name: 'contract_manager.detail', params: { id: contractManagerId } },
                        text: cm && cm.id === parseInt(contractManagerId) ? cm.name : `Contract Manager #${contractManagerId}`
                    }
                ]
            },
            breadcrumbMode: 'replace'
        },
        children: [
            {
                path: '',
                name: 'contractManager.accounting',
                component: AccountingIndex,
                meta: {
                    label: 'Transactions',
                    scopeType: 'contract-manager',
                    permission: 'contract_manager.accounting.transactions.list'
                },
            },
            {
                path: 'order-item/:id',
                name: 'contractManager.accounting.order-item',
                component: OrderItemDetail,
                meta: {
                    label: 'Order Item Detail',
                    scopeType: 'contract-manager',
                    permission: 'contract_manager.accounting.order.detail'
                },
            },
            {
                path: 'service-order/:id',
                name: 'contractManager.accounting.service-order',
                component: ServiceOrderDetail,
                meta: {
                    label: 'Service Order Detail',
                    scopeType: 'contract-manager',
                    permission: 'contract_manager.accounting.service_order.detail'
                },
            },
            {
                path: 'refund/:id',
                name: 'contractManager.accounting.refund',
                component: RefundDetail,
                meta: {
                    label: 'Refund detail',
                    scopeType: 'contract-manager',
                    permission: 'contract_manager.accounting.refund.detail'
                },
            },
            {
                path: 'payout/:id',
                name: 'contractManager.accounting.payout',
                component: PayoutDetail,
                meta: {
                    label: 'Payout Detail',
                    scopeType: 'contract-manager',
                    permission: 'contract_manager.accounting.payout.detail'
                },
            },
        ]
    },

    // Contract Manager scope — Movements
    {
        path: '/contract-manager/:contractManagerId/movements',
        component: TheContainer,
        meta: {
            perms: ['contract_manager.accounting.movements.list'],
            breadcrumb: (context) => {
                const cm = context.store.getters['contractManager/contract_manager']
                const contractManagerId = context.route.params.contractManagerId

                if (!cm || cm.id !== parseInt(contractManagerId)) {
                    context.store.dispatch('contractManager/fetchContractManager', contractManagerId)
                        .catch(() => {})
                }

                return [
                    { to: { name: 'index' }, text: context.gettext('Dashboard') },
                    { to: { name: 'contract_manager.list' }, text: 'Contract Managers' },
                    {
                        to: { name: 'contract_manager.detail', params: { id: contractManagerId } },
                        text: cm && cm.id === parseInt(contractManagerId) ? cm.name : `Contract Manager #${contractManagerId}`
                    }
                ]
            },
            breadcrumbMode: 'replace'
        },
        children: [
            {
                path: '',
                name: 'contractManager.movements',
                component: MovementIndex,
                meta: {
                    label: 'Movements',
                    scopeType: 'contract-manager',
                    permission: 'contract_manager.accounting.movements.list'
                },
            },
            {
                path: 'order-item/:id',
                name: 'contractManager.movements.order-item',
                component: MovementOrderItemDetail,
                meta: {
                    label: 'Order Item Detail',
                    scopeType: 'contract-manager',
                    permission: 'contract_manager.accounting.order.detail',
                    breadcrumb: (context) => ([
                        {
                            text: 'Movements',
                            to: { name: 'contractManager.movements', params: context.route.params }
                        },
                        { text: 'Order Item Detail' }
                    ])
                },
            },
            {
                path: 'refund/:id',
                name: 'contractManager.movements.refund',
                component: MovementRefundDetail,
                meta: {
                    label: 'Refund Detail',
                    scopeType: 'contract-manager',
                    permission: 'contract_manager.accounting.refund.detail',
                    breadcrumb: (context) => ([
                        {
                            text: 'Movements',
                            to: { name: 'contractManager.movements', params: context.route.params }
                        },
                        { text: 'Refund Detail' }
                    ])
                },
            },
        ]
    },

    // Global scope — Movements (admin)
    {
        path: '/admin/movements',
        component: TheContainer,
        meta: { perms: ['admin.accounting.movements.list'] },
        children: [
            {
                path: '',
                name: 'admin.movements',
                component: MovementIndex,
                meta: {
                    label: 'Movements',
                    scopeType: 'global',
                    permission: 'admin.accounting.movements.list'
                },
            },
            {
                path: 'order-item/:id',
                name: 'admin.movements.order-item',
                component: MovementOrderItemDetail,
                meta: {
                    label: 'Order Item Detail',
                    scopeType: 'global',
                    permission: 'admin.accounting.order.detail'
                },
            },
            {
                path: 'refund/:id',
                name: 'admin.movements.refund',
                component: MovementRefundDetail,
                meta: {
                    label: 'Refund Detail',
                    scopeType: 'global',
                    permission: 'admin.accounting.refund.detail'
                },
            },
        ]
    },

    // Note: Pica Service scope routes are defined in eventManage/routes/accounting.js
    // as children of /events/:picaServiceId/
]
